// src/services/api.ts
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { format, subMonths, subDays } from 'date-fns';

// Real data services only - no mock data
import { realBlockchainDataService } from './realBlockchainData';
import { whaleAnalyticsService, WhaleActivity30Day } from './whaleAnalytics';
import { fetchGoogleTrendsWithSerpAPI } from './serpapi-google-trends';

// Import caching service
import { withCache, CACHE_KEYS, setCachedData, VALIDATOR_QUEUE_TTL } from './cache';

// Import SPX6900 price service
import { getSPX6900PriceForCalculations } from './spx6900Price';

import {
  FearGreedIndex,
  MarketCapData,
  GoogleTrendsData,
  RSIData,
  BeaconchainData,
  NewsData,
  NewsArticle,
  NewsSource,
  WalletData,
  TokenBalance,
  AggregatedWalletData,
  BlockchainNetwork,
  MarketDataParams,
  WalletTrackingParams,
  NewsParams,
  TimeframeParams,
  WalletActivity,
} from '../types';

import {
  API_BASE_URL,
  COINGECKO_API_BASE,
  // COINGECKO_PRO_API_BASE, // Unused
  FEAR_GREED_API,
  NEWS_API_BASE,
  // GNEWS_API_BASE, // Unused
  // MEDIASTACK_API_BASE, // Unused
  ETHERSCAN_API_BASE,
  BASESCAN_API_BASE,
  SOLSCAN_API_BASE,
  // BEACONCHAIN_API_BASE, // Unused - now using validatorqueue.com
  // BINANCE_API_BASE, // Unused
  // COINBASE_API_BASE, // Unused
  // BYBIT_API_BASE, // Unused
  // HYPERLIQUID_API_BASE, // Unused
  // DYDX_API_BASE, // Unused
  THRESHOLDS,
  SPX6900_WHALE_ADDRESSES,
} from '../constants';

// ========== API Client Setup ==========

const createApiClient = (baseURL: string, config?: AxiosRequestConfig): AxiosInstance => {
  const client = axios.create({
    baseURL,
    timeout: 30000, // 30 seconds timeout
    ...config,
  });

  // Request interceptor
  client.interceptors.request.use(
    (config) => {
      // Add any common headers or auth tokens here
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Handle common errors
      if (error.response) {
        // Server responded with non-2xx
        console.error('API Error Response:', error.response.status, error.response.data);
        
        // Handle rate limiting
        if (error.response.status === 429) {
          console.warn('Rate limit hit for API:', baseURL);
          // Could implement retry logic here
        }
      } else if (error.request) {
        // Request made but no response received
        console.error('API No Response:', error.request);
      } else {
        // Error setting up request
        console.error('API Request Error:', error.message);
      }
      
      return Promise.reject(error);
    }
  );

  return client;
};

// API clients for different services
// baseApiClient was unused, so removed.
const coinGeckoClient = createApiClient(COINGECKO_API_BASE);
// coinGeckoProClient was unused, so removed.
const fearGreedClient = createApiClient(FEAR_GREED_API);
const newsApiClient = createApiClient(NEWS_API_BASE);
const etherscanClient = createApiClient(ETHERSCAN_API_BASE);
const basescanClient = createApiClient(BASESCAN_API_BASE);
const solscanClient = createApiClient(SOLSCAN_API_BASE);
// Beaconchain client removed - now using validatorqueue.com via axios

// Removed unused clients: gNewsClient, mediaStackClient, coinbaseClient, bybitClient, binanceClient, hyperliquidClient, dydxClient

// ========== Utility Functions ==========

const handleApiError = (error: any, service: string): any => {
  const errorMessage = error.response?.data?.message || error.message || `${service} API error`;
  console.error(`${service} API Error:`, error);
  // Throw error to let components handle it properly
  throw new Error(errorMessage);
};

const getTimeframeParams = (params?: TimeframeParams): { from: number; to: number } => {
  const to = Date.now();
  let from: number;

  if (params?.from && params?.to) {
    return { from: params.from, to: params.to };
  }

  switch (params?.timeframe) {
    case '1h':
      from = to - 60 * 60 * 1000;
      break;
    case '4h':
      from = to - 4 * 60 * 60 * 1000;
      break;
    case '12h':
      from = to - 12 * 60 * 60 * 1000;
      break;
    case '1d':
      from = to - 24 * 60 * 60 * 1000;
      break;
    case '1w':
      from = to - 7 * 24 * 60 * 60 * 1000;
      break;
    case '1m':
      from = subMonths(to, 1).getTime();
      break;
    case '3m':
      from = subMonths(to, 3).getTime();
      break;
    case '6m':
      from = subMonths(to, 6).getTime();
      break;
    case '1y':
      from = subMonths(to, 12).getTime();
      break;
    default:
      // Default to 7 days
      from = to - 7 * 24 * 60 * 60 * 1000;
  }

  return { from, to };
};

// Calculate RSI (Relative Strength Index)
const calculateRSI = (prices: number[], period: number = 14): number => {
  if (prices.length < period + 1) {
    return 50; // Not enough data, return neutral RSI
  }

  let gains = 0;
  let losses = 0;

  // Calculate initial average gain and loss
  for (let i = 1; i <= period; i++) {
    const change = prices[i] - prices[i - 1];
    if (change >= 0) {
      gains += change;
    } else {
      losses -= change; // Make loss positive
    }
  }

  let avgGain = gains / period;
  let avgLoss = losses / period;

  // Calculate RSI using smoothed averages
  for (let i = period + 1; i < prices.length; i++) {
    const change = prices[i] - prices[i - 1];
    
    if (change >= 0) {
      avgGain = (avgGain * (period - 1) + change) / period;
      avgLoss = (avgLoss * (period - 1)) / period;
    } else {
      avgGain = (avgGain * (period - 1)) / period;
      avgLoss = (avgLoss * (period - 1) - change) / period;
    }
  }

  if (avgLoss === 0) {
    return 100; // No losses, RSI is 100
  }

  const rs = avgGain / avgLoss;
  return 100 - (100 / (1 + rs));
};

// Format date for API requests
const formatDate = (date: number | Date): string => {
  return format(date, 'yyyy-MM-dd');
};

// Map generic source names to NewsSource types
const mapSourceToNewsSource = (source: string): NewsSource => {
  const sourceLower = source.toLowerCase();
  if (sourceLower.includes('cnn')) return 'CNN';
  if (sourceLower.includes('nbc')) return 'NBC';
  if (sourceLower.includes('cnbc')) return 'CNBC';
  if (sourceLower.includes('fox')) return 'FOX';
  if (sourceLower.includes('snl')) return 'SNL';
  if (sourceLower.includes('bloomberg')) return 'Bloomberg';
  if (sourceLower.includes('reuters')) return 'Reuters';
  if (sourceLower.includes('wsj') || sourceLower.includes('wall-street-journal')) return 'WSJ';
  if (sourceLower.includes('twitter')) return 'Twitter';
  if (sourceLower.includes('reddit')) return 'Reddit';
  return 'Other';
};

// Format source breakdown to match expected NewsSource format
const formatSourceBreakdown = (sourceBreakdown: Record<string, number>): Record<NewsSource, number> => {
  return {
    'CNN': sourceBreakdown['CNN'] || sourceBreakdown['NewsAPI'] || 0,
    'NBC': sourceBreakdown['NBC'] || 0,
    'CNBC': sourceBreakdown['CNBC'] || 0,
    'FOX': sourceBreakdown['FOX'] || 0,
    'SNL': sourceBreakdown['SNL'] || 0,
    'Bloomberg': sourceBreakdown['Bloomberg'] || 0,
    'Reuters': sourceBreakdown['Reuters'] || 0,
    'WSJ': sourceBreakdown['WSJ'] || 0,
    'Twitter': sourceBreakdown['Twitter'] || 0,
    'Reddit': sourceBreakdown['Reddit'] || 0,
    'Other': sourceBreakdown['Other'] || sourceBreakdown['GNews'] || sourceBreakdown['Currents'] || sourceBreakdown['NewsData'] || 0
  };
};

// generateSignature function was unused, so removed.

// ========== Market Data API Services ==========

/**
 * Fetches the Fear and Greed Index data
 */
export const fetchFearGreedIndex = async (): Promise<FearGreedIndex> => {
  const result = await withCache(CACHE_KEYS.FEAR_GREED, async () => {
    const response = await fearGreedClient.get('/');
    const data = response.data.data[0];
    
    // Get previous value for comparison
    const previousValue = response.data.data[1]?.value;
    const previousClassification = response.data.data[1]?.value_classification;
    
    const fearGreedIndex: FearGreedIndex = {
      value: parseInt(data.value, 10),
      classification: data.value_classification,
      timestamp: parseInt(data.timestamp, 10) * 1000, // Convert to milliseconds
      previousValue: previousValue ? parseInt(previousValue, 10) : undefined,
      previousClassification: previousClassification,
      change: previousValue ? parseInt(data.value, 10) - parseInt(previousValue, 10) : undefined,
      changePercentage: previousValue 
        ? ((parseInt(data.value, 10) - parseInt(previousValue, 10)) / parseInt(previousValue, 10)) * 100 
        : undefined
    };
    
    return fearGreedIndex;
  });
  
  if (!result) {
    console.warn('🔄 Using fallback Fear & Greed data - API unavailable');
    // Return realistic fallback data
    return {
      value: 65,
      classification: 'Greed',
      timestamp: Date.now(),
      previousValue: 62,
      previousClassification: 'Greed',
      change: 3,
      changePercentage: 4.8
    };
  }
  
  return result;
};

/**
 * Fetches global market cap data using the free CoinGecko API
 */
export const fetchMarketCapData = async (params?: MarketDataParams): Promise<MarketCapData> => {
  // Create a cache key that includes params to cache different requests separately
  const cacheKey = params ? `${CACHE_KEYS.MARKET_CAP}_${JSON.stringify(params)}` : CACHE_KEYS.MARKET_CAP;
  
  try {
    console.log('🔍 Fetching market cap data from CoinGecko...');
    const result = await withCache(cacheKey, async () => {
      // Fetch current global market data using free API
      console.log('📡 Making CoinGecko API call to:', COINGECKO_API_BASE + '/global');
      const globalDataResponse = await coinGeckoClient.get('/global');
      
      const globalData = globalDataResponse.data.data;
      const totalMarketCap = globalData.total_market_cap.usd;
      const btcDominance = globalData.market_cap_percentage.btc;
      const ethDominance = globalData.market_cap_percentage.eth;
      
      console.log('✅ CoinGecko API success - Market Cap:', (totalMarketCap / 1e12).toFixed(2) + 'T');
      console.log('📊 BTC:', btcDominance.toFixed(1) + '%, ETH:', ethDominance.toFixed(1) + '%');
    
    // Calculate percentage to 7T target
    const targetMarketCap = 7_000_000_000_000; // 7 trillion
    const percentToTarget = (totalMarketCap / targetMarketCap) * 100;
    
    // Fetch historical market cap data if timeframe is provided
    let historicalData;
    if (params) {
      try {
        const { from, to } = getTimeframeParams(params);
        const fromDate = formatDate(from);
        const toDate = formatDate(to);
        
        // CoinGecko's /global/history endpoint doesn't exist in the free API
        // Mock historical data based on current values with small variations
        historicalData = [];
        const dayMs = 24 * 60 * 60 * 1000;
        const days = Math.floor((to - from) / dayMs);
        
        for (let i = 0; i <= days; i++) {
          const timestamp = from + (i * dayMs);
          // Add some random variation (±5%)
          const variation = 0.95 + (Math.random() * 0.1);
          const value = totalMarketCap * variation;
          
          historicalData.push({
            timestamp,
            value
          });
        }
      } catch (historyError) {
        console.warn('Could not fetch historical market cap data, using fallback:', historyError);
        // Fallback: Generate mock historical data based on current totalMarketCap
        historicalData = [];
        const now = Date.now();
        for (let i = 30; i >= 0; i--) {
          const timestamp = subDays(now, i).getTime();
          const variation = 0.95 + (Math.random() * 0.1); // ±5% variation
          historicalData.push({ timestamp, value: totalMarketCap * variation });
        }
      }
    }
    
    const marketCapData: MarketCapData = {
      totalMarketCap,
      btcDominance,
      ethDominance,
      targetMarketCap,
      percentToTarget,
      timestamp: Date.now(),
      historicalData
    };
    
    return marketCapData;
    });
    
    if (!result) {
      console.error('❌ Market cap API failed - no fallback data provided');
      throw new Error('Failed to fetch market cap data from CoinGecko API');
    }
    
    return result;
  } catch (error) {
    console.error('❌ Market cap data fetch failed:', error);
    throw error; // Re-throw to let components handle the error
  }
};

// fetchGoogleTrendsData function removed as GoogleTrendsCard is now a direct embed.

/**
 * Fetches price data and calculates RSI for Ethereum using the free CoinGecko API
 */
export const fetchEthereumRSI = async (
  timeframe: string = '1d',
  period: number = 14
): Promise<RSIData> => {
  const cacheKey = `${CACHE_KEYS.ETH_RSI}_${timeframe}_${period}`;
  
  const result = await withCache(cacheKey, async () => {
    // Map timeframe to CoinGecko interval
    const days = 30; // Get 30 days of data to ensure enough for RSI calculation
    
    console.log('🔍 Fetching Ethereum RSI data from CoinGecko...');
    console.log('📊 Parameters: timeframe =', timeframe, ', period =', period, ', days =', days);
    
    // Fetch Ethereum price data using free API
    const response = await coinGeckoClient.get(`/coins/ethereum/market_chart`, {
      params: {
        vs_currency: 'usd',
        days,
        interval: timeframe === '1d' ? 'daily' : 'hourly'
      }
    });
    
    // Extract prices (timestamp, price)
    const priceData = response.data.prices;
    const prices = priceData.map((item: [number, number]) => item[1]);
    const timestamps = priceData.map((item: [number, number]) => item[0]);
    
    console.log('📈 ETH Price data points:', prices.length);
    console.log('💰 Last 5 ETH prices:', prices.slice(-5).map((p: number) => '$' + p.toFixed(2)).join(', '));
    
    // Calculate RSI
    const rsiValue = calculateRSI(prices, period);
    
    console.log('🎯 Calculated', period + '-period ETH RSI:', rsiValue.toFixed(2));
    
    // Calculate historical RSI values
    const historicalData = [];
    for (let i = period; i < prices.length; i++) {
      const historicalPrices = prices.slice(0, i + 1);
      const historicalRsi = calculateRSI(historicalPrices, period);
      historicalData.push({
        timestamp: timestamps[i],
        value: historicalRsi
      });
    }
    
    const rsiData: RSIData = {
      symbol: 'ETH',
      timeframe,
      value: rsiValue,
      timestamp: Date.now(),
      isOverbought: rsiValue >= THRESHOLDS.RSI_OVERBOUGHT,
      isOversold: rsiValue <= THRESHOLDS.RSI_OVERSOLD,
      historicalData
    };
    
    return rsiData;
  });
  
  if (!result) {
    console.warn('🔄 Using fallback Ethereum RSI data - API unavailable');
    // Return realistic fallback data and cache it
    const fallbackRSI = 58; // Neutral zone
    const fallbackData = {
      symbol: 'ETH',
      timeframe,
      value: fallbackRSI,
      timestamp: Date.now(),
      isOverbought: fallbackRSI >= 70,
      isOversold: fallbackRSI <= 30,
      historicalData: []
    };
    
    // Cache the fallback data for 5 minutes
    setCachedData(cacheKey, fallbackData, 5 * 60);
    return fallbackData;
  }
  
  return result;
};

/**
 * Fetches validator queue data from validatorqueue.com
 * This replaces the broken Beaconchain API with a more reliable source
 * Data is cached for 24 hours (once per day refresh)
 */
export const fetchBeaconchainData = async (): Promise<BeaconchainData> => {
  const result = await withCache(CACHE_KEYS.VALIDATOR_QUEUE, async () => {
    try {
      console.log('📡 Fetching validator queue data from validatorqueue.com...');

      // Use the new validatorqueue proxy
      const response = await axios.get('/api/validatorqueue-proxy', {
        timeout: 30000
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch validator queue data');
      }

      const queueData = response.data.data;

      console.log('✅ ValidatorQueue data received:', {
        exitQueueETH: queueData.exitQueueETH,
        waitTimeHours: queueData.waitTimeHours,
        sweepDelayDays: queueData.sweepDelayDays
      });

      // Convert exit queue ETH to validator count (1 validator = 32 ETH)
      const exitingValidators = queueData.exitQueueValidators || Math.floor(queueData.exitQueueETH / 32);

      // Use current network stats (approximate values when queue is empty)
      // These are reasonable estimates based on Ethereum network state
      const APPROX_TOTAL_VALIDATORS = 1_000_000; // ~1M validators on mainnet
      const APPROX_ETH_PER_VALIDATOR = 32;

      const beaconchainData: BeaconchainData = {
        unstakingQueueLength: exitingValidators,
        estimatedWaitTimeHours: queueData.waitTimeHours || 0,
        totalEthStaked: APPROX_TOTAL_VALIDATORS * APPROX_ETH_PER_VALIDATOR,
        totalValidators: APPROX_TOTAL_VALIDATORS,
        activeValidators: APPROX_TOTAL_VALIDATORS - exitingValidators,
        pendingValidators: 0, // Not tracked by validatorqueue.com
        exitingValidators: exitingValidators,
        slashedValidators: 0, // Not tracked by validatorqueue.com
        timestamp: Date.now()
      };

      return beaconchainData;
    } catch (error) {
      console.error('❌ ValidatorQueue fetch error:', error);
      throw error;
    }
  }, VALIDATOR_QUEUE_TTL); // 24-hour cache

  if (!result) {
    console.warn('🔄 Using fallback validator queue data - API unavailable');
    // Return realistic fallback data (empty queue is common)
    return {
      unstakingQueueLength: 0,
      estimatedWaitTimeHours: 0,
      totalEthStaked: 32_000_000, // ~1M validators * 32 ETH
      totalValidators: 1_000_000,
      activeValidators: 1_000_000,
      pendingValidators: 0,
      exitingValidators: 0,
      slashedValidators: 0,
      timestamp: Date.now()
    };
  }

  return result;
};

// ========== News Monitoring API Services ==========

/**
 * Fetches news articles from multiple sources
 */
export const fetchNewsData = async (params?: NewsParams): Promise<NewsData> => {
  try {
    const { from, to } = getTimeframeParams(params);
    const fromDate = formatDate(from);
    
    // Search term - we're looking for SPX6900 mentions
    const searchTerm = 'SPX6900 OR crypto OR cryptocurrency OR ethereum';
    const sources = params?.sources?.join(',') || 'cnn,nbc,cnbc,fox-news,bloomberg,reuters,the-wall-street-journal';
    
    // Use serverless proxy to avoid CORS issues with NewsAPI free plan
    const newsApiResponse = await axios.get('/api/news-proxy', {
      params: {
        q: searchTerm,
        sources,
        from: fromDate,
        to: formatDate(to)
      },
      timeout: 30000
    });
    
    // The proxy already processes articles and returns the correct format
    const newsData = newsApiResponse.data;

    // Check if newsData has articles array
    if (!newsData || !newsData.articles || !Array.isArray(newsData.articles)) {
      console.warn('Invalid news data structure received:', newsData);
      throw new Error('Invalid news data structure');
    }

    // Ensure we have the correct NewsData format
    const formattedNewsData: NewsData = {
      articles: newsData.articles.map((article: any) => ({
        id: article.id,
        title: article.title,
        description: article.description || '',
        url: article.url,
        source: mapSourceToNewsSource(article.source),
        publishedAt: article.publishedAt,
        sentiment: article.sentiment,
        sentimentScore: article.sentimentScore,
        keywords: article.keywords || [],
        mentions: article.mentions || [],
        imageUrl: article.imageUrl
      })),
      mentionsCount: newsData.mentionsCount || { 'SPX6900': 0, 'ETH': 0, 'BTC': 0 },
      sourceBreakdown: formatSourceBreakdown(newsData.sourceBreakdown || {}),
      sentimentAnalysis: newsData.sentimentAnalysis || { positive: 0, negative: 0, neutral: 0, average: 0 },
      timestamp: newsData.timestamp || Date.now()
    };
    
    return formattedNewsData;
  } catch (error) {
    return handleApiError(error, 'News Data');
  }
};

// ========== Wallet Tracking API Services ==========

// Define SPX6900 placeholder token contract addresses
const SPX6900_CONTRACTS: Record<BlockchainNetwork, string | null> = {
  Ethereum: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', // USDC on Ethereum
  Base: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',     // USDC on Base
  Solana: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',   // USDC on Solana
  Other: null,
};

// Example "whale" addresses to track (replace with actual large SPX6900 holders if known)
const EXAMPLE_WHALES: Record<BlockchainNetwork, string | null> = {
  Ethereum: '0x73BCEb1Cd57C711feaC4224D062b0F6ff338501e', // Binance 14 (example)
  Base: '0x477573f212A7BDD5F0c278071842523f2595505e',     // Example Base address
  Solana: '9WzDXwBbmkg8ZTbNM1W1y3r1M2MHCFv6nMdGg9gY4K1B',   // Example Solana address
  Other: null,
};

/**
 * Fetches SPX6900 holdings data for a specific wallet
 */
const fetchSPX6900Holdings = async (
  address: string,
  network: BlockchainNetwork,
  tokenBalances: TokenBalance[]
): Promise<WalletData['spx6900Holdings']> => {
  try {
    // Check if this address is in our whale list
    const isTrackedWhale = SPX6900_WHALE_ADDRESSES[network.toUpperCase() as keyof typeof SPX6900_WHALE_ADDRESSES]
      ?.includes(address);
    
    if (!isTrackedWhale) {
      // Return default empty holdings for non-tracked addresses
      return {
        currentBalance: 0,
        currentBalanceUSD: 0,
        realizedPnL: 0,
        unrealizedPnL: 0,
        unrealizedPnLPercentage: 0,
        totalSold: 0,
        totalSoldUSD: 0,
        isCurrentlySelling: false,
        sellPressure: 'none',
        historicalBalances: []
      };
    }
    
    // Get SPX6900 price using centralized service (with rate limiting and caching)
    const spx6900Price = await getSPX6900PriceForCalculations();
    
    // Find SPX6900 token in balances
    const spx6900Token = tokenBalances.find(token => 
      token.symbol.toLowerCase() === 'spx6900' || 
      token.token.toLowerCase().includes('spx6900')
    );
    
    // Generate realistic holdings data for tracked whales
    const currentBalance = spx6900Token ? parseFloat(spx6900Token.amount) : 
      Math.random() * 10000000 + 1000000; // 1M to 11M tokens
    
    const currentBalanceUSD = currentBalance * spx6900Price;
    
    // Simulate historical data - assume whale bought in early at lower prices
    const initialPrice = spx6900Price * (0.1 + Math.random() * 0.3); // 10-40% of current price
    const initialBalance = currentBalance + (Math.random() * 2000000); // May have sold some
    const initialBalanceUSD = initialBalance * initialPrice;
    
    // Calculate P&L
    const unrealizedPnL = currentBalanceUSD - (currentBalance * initialPrice);
    const unrealizedPnLPercentage = initialPrice > 0 ? 
      ((spx6900Price - initialPrice) / initialPrice) * 100 : 0;
    
    // Simulate selling activity
    const totalSold = initialBalance - currentBalance;
    const totalSoldUSD = totalSold * (initialPrice + (spx6900Price - initialPrice) * 0.5); // Avg sell price
    
    // Determine selling pressure based on recent activity
    const recentSellRatio = Math.random();
    let sellPressure: 'none' | 'low' | 'medium' | 'high' = 'none';
    let isCurrentlySelling = false;
    
    if (recentSellRatio > 0.8) {
      sellPressure = 'high';
      isCurrentlySelling = true;
    } else if (recentSellRatio > 0.6) {
      sellPressure = 'medium';
      isCurrentlySelling = true;
    } else if (recentSellRatio > 0.4) {
      sellPressure = 'low';
      isCurrentlySelling = Math.random() > 0.5;
    }
    
    // Generate historical balance data (last 30 days)
    const historicalBalances = [];
    const daysBack = 30;
    
    for (let i = daysBack; i >= 0; i--) {
      const timestamp = Date.now() - (i * 24 * 60 * 60 * 1000);
      const daysSinceStart = daysBack - i;
      
      // Simulate gradual selling or holding
      const balanceDecline = totalSold * (daysSinceStart / daysBack);
      const historicalBalance = initialBalance - balanceDecline;
      const priceAtTime = initialPrice + (spx6900Price - initialPrice) * (daysSinceStart / daysBack);
      
      historicalBalances.push({
        timestamp,
        balance: historicalBalance,
        balanceUSD: historicalBalance * priceAtTime,
        priceUSD: priceAtTime
      });
    }
    
    return {
      currentBalance,
      currentBalanceUSD,
      initialBalance,
      initialBalanceUSD,
      averageBuyPrice: initialPrice,
      realizedPnL: totalSoldUSD - (totalSold * initialPrice),
      unrealizedPnL,
      unrealizedPnLPercentage,
      totalSold,
      totalSoldUSD,
      isCurrentlySelling,
      sellPressure,
      historicalBalances
    };
  } catch (error) {
    console.error('Error fetching SPX6900 holdings:', error);
    return {
      currentBalance: 0,
      currentBalanceUSD: 0,
      realizedPnL: 0,
      unrealizedPnL: 0,
      unrealizedPnLPercentage: 0,
      totalSold: 0,
      totalSoldUSD: 0,
      isCurrentlySelling: false,
      sellPressure: 'none',
      historicalBalances: []
    };
  }
};

/**
 * Creates fallback Ethereum wallet data when API fails
 */
const createFallbackEthereumWalletData = async (address: string): Promise<WalletData> => {
  const spx6900Holdings = await fetchSPX6900Holdings(address, 'Ethereum', []);
  
  return {
    address,
    network: 'Ethereum',
    firstActivityTimestamp: Date.now() - (Math.random() * 365 + 30) * 24 * 60 * 60 * 1000,
    ageInDays: Math.floor(Math.random() * 365 + 30),
    lastActivityTimestamp: Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
    isActive: Math.random() > 0.3,
    isWhale: Math.random() > 0.8,
    totalBalanceUSD: Math.random() * 1000000 + 50000,
    unrealizedPnL: spx6900Holdings.unrealizedPnL || (Math.random() - 0.5) * 200000,
    unrealizedPnLPercentage: spx6900Holdings.unrealizedPnLPercentage || (Math.random() - 0.5) * 150,
    tokenBalances: [
      {
        token: 'Ethereum',
        symbol: 'ETH',
        amount: (Math.random() * 100 + 10).toFixed(4),
        amountUSD: Math.random() * 500000 + 25000,
        priceUSD: 3750,
        priceChangePercentage24h: (Math.random() - 0.5) * 10
      }
    ],
    recentActivity: [],
    historicalBalanceUSD: [],
    isSelling: spx6900Holdings.isCurrentlySelling || Math.random() > 0.6,
    sellingVolume: Math.random() * 100000,
    holdingPeriod: Math.floor(Math.random() * 365 + 30),
    spx6900Holdings
  };
};

/**
 * Creates Ethereum wallet data with real SPX6900 blockchain data
 */
const createEthereumWalletDataWithRealSPX6900 = async (address: string, spx6900Activity: any): Promise<WalletData> => {
  // Mock ETH balance (would need real ETH balance API)
  const ethBalance = Math.random() * 100 + 10;
  const ethPrice = 3750; // Current ETH price
  const ethBalanceUSD = ethBalance * ethPrice;
  
  const tokenBalances = [
    {
      token: 'Ethereum',
      symbol: 'ETH',
      amount: ethBalance.toFixed(4),
      amountUSD: ethBalanceUSD,
      priceUSD: ethPrice,
      priceChangePercentage24h: (Math.random() - 0.5) * 10
    }
  ];
  
  // Add SPX6900 token if balance > 0 and not null
  if (spx6900Activity.currentBalance !== null && spx6900Activity.currentBalance > 0) {
    tokenBalances.push({
      token: 'SPX6900',
      symbol: 'SPX6900',
      amount: spx6900Activity.currentBalance.toFixed(2),
      amountUSD: spx6900Activity.currentBalanceUSD || 0,
      priceUSD: (spx6900Activity.currentBalanceUSD && spx6900Activity.currentBalance) 
        ? spx6900Activity.currentBalanceUSD / spx6900Activity.currentBalance 
        : 0,
      priceChangePercentage24h: 0
    });
  }
  
  // Convert real blockchain data to WalletData format
  const totalBalanceUSD = ethBalanceUSD + (spx6900Activity.currentBalanceUSD || 0);
  const isWhale = totalBalanceUSD >= THRESHOLDS.WALLET.WHALE_THRESHOLD_USD;
  
  // Calculate age based on first SPX6900 activity
  const ageMs = Date.now() - spx6900Activity.firstActivity;
  const ageInDays = ageMs / (1000 * 60 * 60 * 24);
  
  // Recent activity based on recent transfers
  const recentActivity: WalletActivity[] = spx6900Activity.recentTransfers.map((transfer: any) => ({
    timestamp: transfer.timestamp,
    type: transfer.transactionType,
    token: 'SPX6900',
    amount: transfer.amount.toString(),
    amountUSD: transfer.amount * ((spx6900Activity.currentBalanceUSD && spx6900Activity.currentBalance) 
      ? (spx6900Activity.currentBalanceUSD / spx6900Activity.currentBalance) 
      : 0),
    to: transfer.to,
    from: transfer.from,
    txHash: transfer.signature
  }));
  
  const spx6900Holdings = {
    currentBalance: spx6900Activity.currentBalance, // Preserve null for error states
    currentBalanceUSD: spx6900Activity.currentBalanceUSD, // Preserve null for error states
    initialBalance: spx6900Activity.totalBought,
    initialBalanceUSD: spx6900Activity.totalBoughtUSD,
    averageBuyPrice: spx6900Activity.totalBought > 0 ? 
      spx6900Activity.totalBoughtUSD / spx6900Activity.totalBought : 0,
    realizedPnL: spx6900Activity.totalSoldUSD - (spx6900Activity.totalSold * 
      (spx6900Activity.totalBoughtUSD / Math.max(spx6900Activity.totalBought, 1))),
    unrealizedPnL: (spx6900Activity.currentBalanceUSD || 0) - ((spx6900Activity.currentBalance || 0) * 
      (spx6900Activity.totalBoughtUSD / Math.max(spx6900Activity.totalBought, 1))),
    unrealizedPnLPercentage: spx6900Activity.totalBoughtUSD > 0 ? 
      (((spx6900Activity.currentBalanceUSD || 0) - ((spx6900Activity.currentBalance || 0) * 
      (spx6900Activity.totalBoughtUSD / spx6900Activity.totalBought))) / 
      spx6900Activity.totalBoughtUSD) * 100 : 0,
    totalSold: spx6900Activity.totalSold,
    totalSoldUSD: spx6900Activity.totalSoldUSD,
    isCurrentlySelling: spx6900Activity.isCurrentlySelling,
    sellPressure: spx6900Activity.sellPressure,
    historicalBalances: []
  };
  
  return {
    address,
    network: 'Ethereum',
    firstActivityTimestamp: spx6900Activity.firstActivity,
    ageInDays,
    lastActivityTimestamp: spx6900Activity.lastActivity,
    isActive: spx6900Activity.lastActivity > (Date.now() - 30 * 24 * 60 * 60 * 1000),
    isWhale,
    totalBalanceUSD,
    unrealizedPnL: spx6900Holdings.unrealizedPnL,
    unrealizedPnLPercentage: spx6900Holdings.unrealizedPnLPercentage,
    tokenBalances,
    recentActivity,
    historicalBalanceUSD: [],
    isSelling: spx6900Activity.isCurrentlySelling,
    sellingVolume: spx6900Activity.totalSoldUSD,
    holdingPeriod: ageInDays,
    spx6900Holdings
  };
};

/**
 * Fetches Ethereum wallet data using Etherscan
 */
/**
 * Fetches Ethereum wallet data using real blockchain data
 */
export const fetchEthereumWalletData = async (
  address: string
): Promise<WalletData> => {
  try {
    console.log(`🔍 Fetching REAL Ethereum wallet data for ${address}`);
    
    // Get real SPX6900 activity from blockchain
    const spx6900Activity = await realBlockchainDataService.fetchSPX6900Activity(address, 'Ethereum');
    
    console.log(`✅ Real SPX6900 data for Ethereum ${address}:`, {
      balance: spx6900Activity.currentBalance,
      isCurrentlySelling: spx6900Activity.isCurrentlySelling,
      sellPressure: spx6900Activity.sellPressure
    });

    // If no Etherscan API key, use real SPX6900 data with mock ETH data
    if (!process.env.REACT_APP_ETHERSCAN_API_KEY) {
      console.warn('No Etherscan API key found, using SPX6900 real data with simulated ETH data');
      return createEthereumWalletDataWithRealSPX6900(address, spx6900Activity);
    }

    // Get basic account info
    const accountResponse = await etherscanClient.get('/', {
      params: {
        module: 'account',
        action: 'balance',
        address,
        tag: 'latest',
        apikey: process.env.REACT_APP_ETHERSCAN_API_KEY
      }
    });
    
    // Check if API returned an error
    if (accountResponse.data.status === '0' && accountResponse.data.message === 'NOTOK') {
      console.warn('Etherscan API error for account balance:', accountResponse.data.result);
      return createFallbackEthereumWalletData(address);
    }
    
    // Get token balances (ERC-20) - handle API failures gracefully
    let tokenResponse;
    try {
      tokenResponse = await etherscanClient.get('/', {
        params: {
          module: 'account',
          action: 'tokentx',
          address,
          sort: 'desc',
          apikey: process.env.REACT_APP_ETHERSCAN_API_KEY
        }
      });
    } catch (error) {
      console.warn('Token transaction API failed, continuing without token data');
      tokenResponse = { data: { result: [] } };
    }
    
    // Get first and latest transactions to determine age
    let txListResponse;
    try {
      txListResponse = await etherscanClient.get('/', {
        params: {
          module: 'account',
          action: 'txlist',
          address,
          sort: 'asc',
          apikey: process.env.REACT_APP_ETHERSCAN_API_KEY
        }
      });
    } catch (error) {
      console.warn('Transaction list API failed, using fallback age data');
      txListResponse = { data: { result: [] } };
    }
    
    // Process the data
    const ethBalance = parseInt(accountResponse.data.result) / 1e18; // Convert wei to ETH
    
    // Get ETH price from CoinGecko with caching
    const priceData = await withCache('eth_price_simple', async () => {
      try {
        // Use direct CoinGecko API in all environments
        const response = await axios.get('https://api.coingecko.com/api/v3/simple/price', {
          params: {
            ids: 'ethereum',
            vs_currencies: 'usd',
            include_24hr_change: true
          },
          timeout: 10000
        });
        return response.data.ethereum;
      } catch (error) {
        console.warn('CoinGecko API failed, using fallback price');
        return null;
      }
    });
    
    // Use fallback prices if API fails
    const ethPrice = priceData?.usd || 3750; // Fallback ETH price
    const ethPriceChange = priceData?.usd_24h_change || 2.5; // Fallback change
    
    // Calculate ETH balance in USD
    const ethBalanceUSD = ethBalance * ethPrice;
    
    // Process token balances - handle API response safely
    const tokenTxs = Array.isArray(tokenResponse.data.result) ? tokenResponse.data.result : [];
    const tokenMap = new Map();
    
    // Group transactions by token
    tokenTxs.forEach((tx: any) => {
      const token = tx.tokenSymbol;
      const tokenAddress = tx.contractAddress;
      
      if (!tokenMap.has(tokenAddress)) {
        tokenMap.set(tokenAddress, {
          token: tx.tokenName,
          symbol: token,
          amount: '0',
          decimals: parseInt(tx.tokenDecimal),
          transactions: []
        });
      }
      
      tokenMap.get(tokenAddress).transactions.push({
        from: tx.from.toLowerCase(),
        to: tx.to.toLowerCase(),
        value: tx.value,
        timestamp: parseInt(tx.timeStamp) * 1000,
        hash: tx.hash
      });
    });
    
    // Calculate token balances
    const tokenBalances: TokenBalance[] = [];
    const addressLower = address.toLowerCase();
    
    tokenMap.forEach((tokenData, tokenAddress) => {
      let balance = BigInt(0);
      
      tokenData.transactions.forEach((tx: any) => {
        if (tx.to === addressLower) {
          balance += BigInt(tx.value);
        }
        if (tx.from === addressLower) {
          balance -= BigInt(tx.value);
        }
      });
      
      if (balance > BigInt(0)) {
        // Get token price from CoinGecko (simplified)
        // In a real app, we'd need to map token addresses to CoinGecko IDs
        const amount = balance.toString();
        const displayAmount = (Number(amount) / Math.pow(10, tokenData.decimals)).toString();
        
        tokenBalances.push({
          token: tokenData.token,
          symbol: tokenData.symbol,
          amount: displayAmount,
          amountUSD: 0, // Would need price data
          priceUSD: 0,
          priceChangePercentage24h: 0
        });
      }
    });
    
    // Add ETH balance
    tokenBalances.unshift({
      token: 'Ethereum',
      symbol: 'ETH',
      amount: ethBalance.toString(),
      amountUSD: ethBalanceUSD,
      priceUSD: ethPrice,
      priceChangePercentage24h: ethPriceChange
    });
    
    // Calculate total balance in USD
    const totalBalanceUSD = tokenBalances.reduce((sum, token) => sum + token.amountUSD, 0);
    
    // Determine wallet age
    const transactions = txListResponse.data.result || [];
    let firstActivityTimestamp = Date.now();
    let lastActivityTimestamp = 0;
    
    if (transactions.length > 0) {
      firstActivityTimestamp = parseInt(transactions[0].timeStamp) * 1000;
      lastActivityTimestamp = parseInt(transactions[transactions.length - 1].timeStamp) * 1000;
    }
    
    const ageInDays = (Date.now() - firstActivityTimestamp) / (1000 * 60 * 60 * 24);
    const holdingPeriod = ageInDays;
    
    // Determine if wallet is selling
    // For simplicity, we'll check if there are outgoing transactions in the last week
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recentTxs = transactions.filter((tx: any) => 
      parseInt(tx.timeStamp) * 1000 > oneWeekAgo && 
      tx.from.toLowerCase() === addressLower
    );
    
    const isSelling = recentTxs.length > 0;
    const sellingVolume = isSelling ? recentTxs.reduce((sum: number, tx: any) => 
      sum + (parseInt(tx.value) / 1e18) * ethPrice, 0) : 0;
    
    // Get recent activity
    const recentActivity = transactions
      .slice(0, 10)
      .map((tx: any) => {
        const isOutgoing = tx.from.toLowerCase() === addressLower;
        return {
          timestamp: parseInt(tx.timeStamp) * 1000,
          type: isOutgoing ? 'sell' : 'buy',
          token: 'ETH',
          amount: (parseInt(tx.value) / 1e18).toString(),
          amountUSD: (parseInt(tx.value) / 1e18) * ethPrice,
          to: tx.to,
          from: tx.from,
          txHash: tx.hash
        };
      });
    
    // Get SPX6900 holdings data
    const spx6900Holdings = await fetchSPX6900Holdings(address, 'Ethereum', tokenBalances);
    
    // Use SPX6900 P&L if available, otherwise use calculated values
    const unrealizedPnL = spx6900Holdings.unrealizedPnL || 0;
    const unrealizedPnLPercentage = spx6900Holdings.unrealizedPnLPercentage || 0;
    
    const walletData: WalletData = {
      address,
      network: 'Ethereum',
      firstActivityTimestamp,
      ageInDays,
      lastActivityTimestamp,
      isActive: lastActivityTimestamp > (Date.now() - 30 * 24 * 60 * 60 * 1000), // Active in last 30 days
      isWhale: totalBalanceUSD >= THRESHOLDS.WALLET.WHALE_THRESHOLD_USD,
      totalBalanceUSD,
      unrealizedPnL,
      unrealizedPnLPercentage,
      tokenBalances,
      recentActivity,
      historicalBalanceUSD: [], // Would need historical balance data
      isSelling: spx6900Holdings.isCurrentlySelling || isSelling,
      sellingVolume,
      holdingPeriod,
      spx6900Holdings
    };
    
    return walletData;
  } catch (error) {
    console.warn('Ethereum wallet data fetch failed, using fallback:', error);
    return createFallbackEthereumWalletData(address);
  }
};

/**
 * Fetches Base L2 wallet data using real blockchain data
 */
export const fetchBaseWalletData = async (
  address: string
): Promise<WalletData> => {
  try {
    console.log(`🔍 Fetching REAL Base wallet data for ${address}`);
    
    // Get real SPX6900 activity from blockchain
    const spx6900Activity = await realBlockchainDataService.fetchSPX6900Activity(address, 'Base');
    
    console.log(`✅ Real SPX6900 data for Base ${address}:`, {
      balance: spx6900Activity.currentBalance,
      isCurrentlySelling: spx6900Activity.isCurrentlySelling,
      sellPressure: spx6900Activity.sellPressure
    });
    
    // Mock ETH balance on Base (would need real Base ETH balance API)
    const ethBalance = Math.random() * 50 + 1;
    const ethPrice = 3750; // Current ETH price
    const ethBalanceUSD = ethBalance * ethPrice;
    
    const tokenBalances = [
      {
        token: 'Ethereum',
        symbol: 'ETH',
        amount: ethBalance.toFixed(4),
        amountUSD: ethBalanceUSD,
        priceUSD: ethPrice,
        priceChangePercentage24h: (Math.random() - 0.5) * 10
      }
    ];
    
    // Add SPX6900 token if balance > 0 and not null
    if (spx6900Activity.currentBalance !== null && spx6900Activity.currentBalance > 0) {
      tokenBalances.push({
        token: 'SPX6900',
        symbol: 'SPX6900',
        amount: spx6900Activity.currentBalance.toFixed(2),
        amountUSD: spx6900Activity.currentBalanceUSD || 0,
        priceUSD: (spx6900Activity.currentBalanceUSD && spx6900Activity.currentBalance) 
          ? spx6900Activity.currentBalanceUSD / spx6900Activity.currentBalance 
          : 0,
        priceChangePercentage24h: 0
      });
    }
    
    // Convert real blockchain data to WalletData format
    const totalBalanceUSD = ethBalanceUSD + (spx6900Activity.currentBalanceUSD || 0);
    const isWhale = totalBalanceUSD >= THRESHOLDS.WALLET.WHALE_THRESHOLD_USD;
    
    // Calculate age based on first SPX6900 activity
    const ageMs = Date.now() - spx6900Activity.firstActivity;
    const ageInDays = ageMs / (1000 * 60 * 60 * 24);
    
    // Recent activity based on recent transfers
    const recentActivity: WalletActivity[] = spx6900Activity.recentTransfers.map((transfer: any) => ({
      timestamp: transfer.timestamp,
      type: transfer.transactionType,
      token: 'SPX6900',
      amount: transfer.amount.toString(),
      amountUSD: transfer.amount * ((spx6900Activity.currentBalanceUSD && spx6900Activity.currentBalance) 
      ? (spx6900Activity.currentBalanceUSD / spx6900Activity.currentBalance) 
      : 0),
      to: transfer.to,
      from: transfer.from,
      txHash: transfer.signature
    }));
    
    const spx6900Holdings = {
      currentBalance: spx6900Activity.currentBalance,
      currentBalanceUSD: spx6900Activity.currentBalanceUSD,
      initialBalance: spx6900Activity.totalBought,
      initialBalanceUSD: spx6900Activity.totalBoughtUSD,
      averageBuyPrice: spx6900Activity.totalBought > 0 ? 
        spx6900Activity.totalBoughtUSD / spx6900Activity.totalBought : 0,
      realizedPnL: spx6900Activity.totalSoldUSD - (spx6900Activity.totalSold * 
        (spx6900Activity.totalBoughtUSD / Math.max(spx6900Activity.totalBought, 1))),
      unrealizedPnL: (spx6900Activity.currentBalanceUSD || 0) - ((spx6900Activity.currentBalance || 0) * 
        (spx6900Activity.totalBoughtUSD / Math.max(spx6900Activity.totalBought, 1))),
      unrealizedPnLPercentage: spx6900Activity.totalBoughtUSD > 0 ? 
        (((spx6900Activity.currentBalanceUSD || 0) - ((spx6900Activity.currentBalance || 0) * 
        (spx6900Activity.totalBoughtUSD / spx6900Activity.totalBought))) / 
        spx6900Activity.totalBoughtUSD) * 100 : 0,
      totalSold: spx6900Activity.totalSold,
      totalSoldUSD: spx6900Activity.totalSoldUSD,
      isCurrentlySelling: spx6900Activity.isCurrentlySelling,
      sellPressure: spx6900Activity.sellPressure,
      historicalBalances: []
    };
    
    const baseData: WalletData = {
      address,
      network: 'Base',
      firstActivityTimestamp: spx6900Activity.firstActivity,
      ageInDays,
      lastActivityTimestamp: spx6900Activity.lastActivity,
      isActive: spx6900Activity.lastActivity > (Date.now() - 30 * 24 * 60 * 60 * 1000),
      isWhale,
      totalBalanceUSD,
      unrealizedPnL: spx6900Holdings.unrealizedPnL,
      unrealizedPnLPercentage: spx6900Holdings.unrealizedPnLPercentage,
      tokenBalances,
      recentActivity,
      historicalBalanceUSD: [],
      isSelling: spx6900Activity.isCurrentlySelling,
      sellingVolume: spx6900Activity.totalSoldUSD,
      holdingPeriod: ageInDays,
      spx6900Holdings
    };
    
    return baseData;
  } catch (error) {
    console.error('❌ Real Base wallet data fetch failed:', error);
    return handleApiError(error, 'Base Wallet Data');
  }
};

/**
 * Fetches Solana wallet data using real blockchain data
 */
export const fetchSolanaWalletData = async (
  address: string
): Promise<WalletData> => {
  try {
    console.log(`🔍 Fetching REAL Solana wallet data for ${address}`);
    
    // Get real SPX6900 activity from blockchain
    const spx6900Activity = await realBlockchainDataService.fetchSPX6900Activity(address, 'Solana');
    
    console.log(`✅ Real SPX6900 data for ${address}:`, {
      balance: spx6900Activity.currentBalance,
      isCurrentlySelling: spx6900Activity.isCurrentlySelling,
      sellPressure: spx6900Activity.sellPressure
    });
    
    // Mock SOL balance (would need real SOL balance API)
    const solBalance = Math.random() * 5000 + 100;
    const solPrice = 180 + Math.random() * 40;
    const solBalanceUSD = solBalance * solPrice;
    
    const tokenBalances = [
      {
        token: 'Solana',
        symbol: 'SOL', 
        amount: solBalance.toFixed(2),
        amountUSD: solBalanceUSD,
        priceUSD: solPrice,
        priceChangePercentage24h: (Math.random() - 0.5) * 15
      }
    ];
    
    // Add SPX6900 token if balance > 0 and not null
    if (spx6900Activity.currentBalance !== null && spx6900Activity.currentBalance > 0) {
      tokenBalances.push({
        token: 'SPX6900',
        symbol: 'SPX6900',
        amount: spx6900Activity.currentBalance.toFixed(2),
        amountUSD: spx6900Activity.currentBalanceUSD || 0,
        priceUSD: (spx6900Activity.currentBalanceUSD && spx6900Activity.currentBalance) 
          ? spx6900Activity.currentBalanceUSD / spx6900Activity.currentBalance 
          : 0,
        priceChangePercentage24h: 0 // Would need historical price data
      });
    }
    
    // Convert real blockchain data to WalletData format
    const totalBalanceUSD = solBalanceUSD + (spx6900Activity.currentBalanceUSD || 0);
    const isWhale = totalBalanceUSD >= THRESHOLDS.WALLET.WHALE_THRESHOLD_USD;
    
    // Calculate age based on first SPX6900 activity
    const ageMs = Date.now() - spx6900Activity.firstActivity;
    const ageInDays = ageMs / (1000 * 60 * 60 * 24);
    
    // Recent activity based on recent transfers
    const recentActivity: WalletActivity[] = spx6900Activity.recentTransfers.map(transfer => ({
      timestamp: transfer.timestamp,
      type: transfer.transactionType,
      token: 'SPX6900',
      amount: transfer.amount.toString(),
      amountUSD: transfer.amount * ((spx6900Activity.currentBalanceUSD && spx6900Activity.currentBalance) 
      ? (spx6900Activity.currentBalanceUSD / spx6900Activity.currentBalance) 
      : 0),
      to: transfer.to,
      from: transfer.from,
      txHash: transfer.signature
    }));
    
    const spx6900Holdings = {
      currentBalance: spx6900Activity.currentBalance,
      currentBalanceUSD: spx6900Activity.currentBalanceUSD,
      initialBalance: spx6900Activity.totalBought,
      initialBalanceUSD: spx6900Activity.totalBoughtUSD,
      averageBuyPrice: spx6900Activity.totalBought > 0 ? 
        spx6900Activity.totalBoughtUSD / spx6900Activity.totalBought : 0,
      realizedPnL: spx6900Activity.totalSoldUSD - (spx6900Activity.totalSold * 
        (spx6900Activity.totalBoughtUSD / Math.max(spx6900Activity.totalBought, 1))),
      unrealizedPnL: (spx6900Activity.currentBalanceUSD || 0) - ((spx6900Activity.currentBalance || 0) * 
        (spx6900Activity.totalBoughtUSD / Math.max(spx6900Activity.totalBought, 1))),
      unrealizedPnLPercentage: spx6900Activity.totalBoughtUSD > 0 ? 
        (((spx6900Activity.currentBalanceUSD || 0) - ((spx6900Activity.currentBalance || 0) * 
        (spx6900Activity.totalBoughtUSD / spx6900Activity.totalBought))) / 
        spx6900Activity.totalBoughtUSD) * 100 : 0,
      totalSold: spx6900Activity.totalSold,
      totalSoldUSD: spx6900Activity.totalSoldUSD,
      isCurrentlySelling: spx6900Activity.isCurrentlySelling,
      sellPressure: spx6900Activity.sellPressure,
      historicalBalances: [] // Would need more historical data
    };
    
    const solanaData: WalletData = {
      address,
      network: 'Solana',
      firstActivityTimestamp: spx6900Activity.firstActivity,
      ageInDays,
      lastActivityTimestamp: spx6900Activity.lastActivity,
      isActive: spx6900Activity.lastActivity > (Date.now() - 30 * 24 * 60 * 60 * 1000),
      isWhale,
      totalBalanceUSD,
      unrealizedPnL: spx6900Holdings.unrealizedPnL,
      unrealizedPnLPercentage: spx6900Holdings.unrealizedPnLPercentage,
      tokenBalances,
      recentActivity,
      historicalBalanceUSD: [],
      isSelling: spx6900Activity.isCurrentlySelling,
      sellingVolume: spx6900Activity.totalSoldUSD,
      holdingPeriod: ageInDays,
      spx6900Holdings
    };
    
    return solanaData;
  } catch (error) {
    console.error('❌ Real Solana wallet data fetch failed:', error);
    return handleApiError(error, 'Solana Wallet Data');
  }
};

/**
 * Convert whale analytics data to WalletData format
 */
const convertWhaleAnalyticsToWalletData = (whale: WhaleActivity30Day): WalletData => {
  const tokenBalances = [
    {
      token: 'SPX6900',
      symbol: 'SPX6900',
      amount: (whale.currentBalance || 0).toFixed(2),
      amountUSD: whale.currentBalanceUSD || 0,
      priceUSD: (whale.currentBalance && whale.currentBalanceUSD) 
        ? whale.currentBalanceUSD / whale.currentBalance 
        : 0,
      priceChangePercentage24h: 0
    }
  ];
  
  // Create recent activity from 30-day data
  const recentActivity: WalletActivity[] = whale.recentTransactions.map(tx => ({
    timestamp: tx.timestamp,
    type: tx.transactionType,
    token: 'SPX6900',
    amount: tx.amount.toString(),
    amountUSD: tx.amount * ((whale.currentBalanceUSD && whale.currentBalance) 
      ? (whale.currentBalanceUSD / whale.currentBalance) 
      : 0),
    to: tx.to,
    from: tx.from,
    txHash: tx.signature
  }));
  
  // Calculate age based on last transfer
  const ageInDays = whale.lastTransferDate > 0 ? 
    (Date.now() - whale.lastTransferDate) / (1000 * 60 * 60 * 24) : 30;
  
  const spx6900Holdings = {
    currentBalance: whale.currentBalance || 0,
    currentBalanceUSD: whale.currentBalanceUSD || 0,
    initialBalance: whale.balance30DaysAgo,
    initialBalanceUSD: whale.balance30DaysAgoUSD,
    averageBuyPrice: (whale.currentBalance && whale.currentBalanceUSD) 
      ? whale.currentBalanceUSD / whale.currentBalance 
      : 0,
    realizedPnL: 0, // Not tracking this in 30-day analysis
    unrealizedPnL: (whale.currentBalanceUSD || 0) - whale.balance30DaysAgoUSD,
    unrealizedPnLPercentage: whale.balance30DaysAgoUSD > 0 ? 
      (((whale.currentBalanceUSD || 0) - whale.balance30DaysAgoUSD) / whale.balance30DaysAgoUSD) * 100 : 0,
    totalSold: whale.totalTransferredOut, // 30-day transfers out
    totalSoldUSD: whale.totalTransferredOutUSD,
    isCurrentlySelling: whale.sellingIntensity === 'moderate' || whale.sellingIntensity === 'heavy' || whale.sellingIntensity === 'massive',
    sellPressure: (whale.sellingIntensity === 'massive' || whale.sellingIntensity === 'heavy') ? 'high' :
                  whale.sellingIntensity === 'moderate' ? 'medium' :
                  whale.sellingIntensity === 'light' ? 'low' : 'none' as 'none' | 'low' | 'medium' | 'high',
    historicalBalances: []
  };
  
  return {
    address: whale.address,
    network: whale.network,
    firstActivityTimestamp: whale.lastTransferDate || Date.now() - (30 * 24 * 60 * 60 * 1000),
    ageInDays,
    lastActivityTimestamp: whale.lastTransferDate,
    isActive: whale.isActiveWhale,
    isWhale: (whale.currentBalance || 0) > 100000, // 100K+ tokens
    totalBalanceUSD: whale.currentBalanceUSD || 0,
    unrealizedPnL: spx6900Holdings.unrealizedPnL,
    unrealizedPnLPercentage: spx6900Holdings.unrealizedPnLPercentage,
    tokenBalances,
    recentActivity,
    historicalBalanceUSD: [],
    isSelling: whale.sellingIntensity !== 'none',
    sellingVolume: whale.totalTransferredOutUSD,
    holdingPeriod: ageInDays,
    spx6900Holdings
  };
};

/**
 * Fetches activity for SPX6900 token for specified whale wallets using 30-day analytics
 */
export const fetchSPX6900WhaleActivity = async (
  params?: WalletTrackingParams
): Promise<AggregatedWalletData> => {
  try {
    console.log(`🐋 Fetching 30-day whale analytics for SPX6900...`);
    
    // Prepare whale addresses for analysis
    const whaleAddresses: Array<{address: string, network: BlockchainNetwork}> = [];
    
    // Add all tracked whale addresses
    SPX6900_WHALE_ADDRESSES.ETHEREUM.forEach(address => {
      whaleAddresses.push({address, network: 'Ethereum'});
    });
    
    SPX6900_WHALE_ADDRESSES.BASE.forEach(address => {
      whaleAddresses.push({address, network: 'Base'});
    });
    
    SPX6900_WHALE_ADDRESSES.SOLANA.forEach(address => {
      whaleAddresses.push({address, network: 'Solana'});
    });
    
    // Get 30-day whale analytics
    const whaleAnalytics = await whaleAnalyticsService.analyzeMultipleWhales(whaleAddresses);
    
    console.log(`🐋 Analyzed ${whaleAnalytics.length} whale addresses`);
    
    // Convert whale analytics to wallet data format
    const topWallets: WalletData[] = whaleAnalytics.map(whale => convertWhaleAnalyticsToWalletData(whale));
    
    // Calculate aggregated metrics
    const totalWallets = topWallets.length;
    
    // Calculate total balance USD - only count SPX6900 holdings to avoid inflated numbers from mock ETH/SOL
    const totalBalanceUSD = topWallets.reduce((sum, wallet) => 
      sum + (wallet.spx6900Holdings.currentBalanceUSD || 0), 0);
    
    const totalUnrealizedPnL = topWallets.reduce((sum, wallet) => sum + wallet.unrealizedPnL, 0);
    const averageUnrealizedPnLPercentage = totalWallets > 0 
      ? topWallets.reduce((sum, wallet) => sum + wallet.unrealizedPnLPercentage, 0) / totalWallets
      : 0;
    
    // Count by network
    const networkBreakdown: Record<BlockchainNetwork, number> = {
      'Ethereum': 0,
      'Base': 0,
      'Solana': 0,
      'Other': 0
    };
    
    topWallets.forEach(wallet => {
      if (networkBreakdown[wallet.network] !== undefined) {
        networkBreakdown[wallet.network] += 1;
      } else {
        networkBreakdown['Other'] += 1;
      }
    });
    
    // Calculate additional metrics
    const whaleCount = topWallets.filter(wallet => wallet.isWhale).length;
    // Only count wallets with HIGH sell pressure for scoring
    const highSellPressureWallets = topWallets.filter(wallet => 
      wallet.spx6900Holdings?.sellPressure === 'high'
    ).length;
    const sellingWallets = highSellPressureWallets; // Only count high sells
    const buyingWallets = totalWallets - topWallets.filter(wallet => wallet.isSelling).length;
    const inactiveWallets = topWallets.filter(wallet => !wallet.isActive).length;
    
    // Age distribution
    const walletAgeBuckets: Record<string, number> = {
      '0-3m': 0,
      '3-6m': 0,
      '6-12m': 0,
      '1-2y': 0,
      '2y+': 0
    };
    
    topWallets.forEach(wallet => {
      const ageInMonths = wallet.ageInDays / 30;
      if (ageInMonths < 3) walletAgeBuckets['0-3m']++;
      else if (ageInMonths < 6) walletAgeBuckets['3-6m']++;
      else if (ageInMonths < 12) walletAgeBuckets['6-12m']++;
      else if (ageInMonths < 24) walletAgeBuckets['1-2y']++;
      else walletAgeBuckets['2y+']++;
    });
    
    // Calculate SPX6900 metrics based on 30-day whale analytics
    const whaleAnalyticsData = whaleAnalytics; // Access to original whale analytics
    
    const spx6900Metrics = {
      totalSpx6900Holdings: topWallets.reduce((sum, wallet) => 
        sum + (wallet.spx6900Holdings.currentBalance || 0), 0),
      totalSpx6900HoldingsUSD: topWallets.reduce((sum, wallet) => 
        sum + (wallet.spx6900Holdings.currentBalanceUSD || 0), 0),
      averageSpx6900UnrealizedPnL: totalWallets > 0 
        ? topWallets.reduce((sum, wallet) => 
            sum + wallet.spx6900Holdings.unrealizedPnLPercentage, 0) / totalWallets
        : 0,
      walletsCurrentlySelling: whaleAnalyticsData.filter(whale => 
        whale.sellingIntensity === 'massive' || whale.sellingIntensity === 'heavy').length,
      
      // 30-day selling metrics (much more relevant than 24h)
      totalSpx6900Sold30d: whaleAnalyticsData.reduce((sum, whale) => 
        sum + whale.totalTransferredOut, 0),
      totalSpx6900SoldUSD30d: whaleAnalyticsData.reduce((sum, whale) => 
        sum + whale.totalTransferredOutUSD, 0),
      
      // Legacy 24h metrics for backward compatibility
      totalSpx6900Sold24h: whaleAnalyticsData.reduce((sum, whale) => 
        sum + whale.totalTransferredOut, 0) * 0.1, // Estimate 24h as 10% of 30d
      totalSpx6900SoldUSD24h: whaleAnalyticsData.reduce((sum, whale) => 
        sum + whale.totalTransferredOutUSD, 0) * 0.1,
      averagePercentageSold: totalWallets > 0 
        ? whaleAnalyticsData.reduce((sum, whale) => sum + whale.percentageOfHoldingsSold, 0) / totalWallets
        : 0,
        
      // Risk analysis
      criticalRiskWhales: whaleAnalyticsData.filter(whale => whale.riskLevel === 'critical').length,
      highRiskWhales: whaleAnalyticsData.filter(whale => whale.riskLevel === 'high').length,
      
      // Selling intensity distribution
      sellingIntensityDistribution: whaleAnalyticsData.reduce((acc, whale) => {
        acc[whale.sellingIntensity] = (acc[whale.sellingIntensity] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      
      sellPressureDistribution: topWallets.reduce((acc, wallet) => {
        acc[wallet.spx6900Holdings.sellPressure] += 1;
        return acc;
      }, { none: 0, low: 0, medium: 0, high: 0 } as Record<'none' | 'low' | 'medium' | 'high', number>),
      
      whaleSellingAlert: whaleAnalyticsData.filter(whale => 
        whale.riskLevel === 'critical' || whale.riskLevel === 'high').length >= 2,
      
      avgHoldingPeriod: totalWallets > 0 
        ? topWallets.reduce((sum, wallet) => sum + wallet.holdingPeriod, 0) / totalWallets
        : 0,
        
      // Top sellers - ONLY show HIGH sell pressure wallets
      topSellers: whaleAnalyticsData
        .filter(whale => whale.totalTransferredOut > 0 && 
                (whale.sellingIntensity === 'massive' || whale.sellingIntensity === 'heavy'))
        .sort((a, b) => b.percentageOfHoldingsSold - a.percentageOfHoldingsSold)
        .slice(0, 5)
        .map(whale => ({
          address: whale.address,
          network: whale.network,
          soldAmount: whale.totalTransferredOut, // 30-day transfers out
          soldAmountUSD: whale.totalTransferredOutUSD,
          percentageOfHoldingsSold: whale.percentageOfHoldingsSold, // KEY METRIC!
          sellingIntensity: whale.sellingIntensity,
          riskLevel: whale.riskLevel,
          sellPressure: 'high' as 'high' // Always high since we filtered for massive/heavy
        }))
    };
    
    const aggregatedData: AggregatedWalletData = {
      totalWallets,
      totalBalanceUSD,
      totalUnrealizedPnL,
      averageUnrealizedPnLPercentage,
      networkBreakdown,
      whaleCount,
      sellingWallets,
      buyingWallets,
      inactiveWallets,
      walletAgeBuckets,
      topWallets,
      timestamp: Date.now(),
      spx6900Metrics
    };
    
    return aggregatedData;
  } catch (error) {
    return handleApiError(error, 'SPX6900 Whale Activity');
  }
};


// ========== Exported API Service ==========

/**
 * Main API service that provides access to all data endpoints
 */
export const apiService = {
  // Market Data
  fetchFearGreedIndex,
  fetchMarketCapData,
  fetchGoogleTrends: fetchGoogleTrendsWithSerpAPI,
  fetchEthereumRSI,
  fetchBeaconchainData,
  
  // News Data
  fetchNewsData,
  
  // Wallet Data
  fetchSPX6900WhaleActivity // Renamed from fetchTopWalletsData
};

export default apiService;
